import { useState } from 'react';
import { HiDotsVertical } from 'react-icons/hi';
import { Button } from '../../../../components/Button';
import { MaterialIcon } from '@/components/MaterialIcon';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import useDeleteOrganizationMember from '../api/useDeleteOrganizationMember';
import { showSuccessToast, showErrorToast } from '../../job_change_processes/lib/toast';

type Props = {
  staffId: number;
  refetchOrganization: () => void;
  onEditStaff: (staffId: number) => void;
};

export default function StaffActionMenu({ staffId, refetchOrganization, onEditStaff }: Props) {
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const { mutateAsync: deleteMember } = useDeleteOrganizationMember();

  const handleDelete = async () => {
    try {
      await deleteMember({ staff_id: staffId });
      setIsDeleteDialogOpen(false);
      showSuccessToast('メンバーを削除しました');
      await refetchOrganization();
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'メンバーの削除に失敗しました';
      showErrorToast(errorMessage);
      setIsDeleteDialogOpen(false);
    }
  };

  return (
    <>
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <Button type="button" color="secondary" size="sm" iconOnly aria-label="メンバー操作">
            <HiDotsVertical className="size-icon-xxs text-gray-500" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          side="bottom"
          sideOffset={4}
          onCloseAutoFocus={(event) => event.preventDefault()}
          className="z-[9999]! w-32 rounded-sm! border-0! p-0! shadow-xl! ring-1 ring-black/5 focus:outline-hidden data-[state=closed]:duration-75 data-[state=closed]:ease-in data-[state=open]:duration-100 data-[state=open]:ease-out"
        >
          <div className="py-1">
            <DropdownMenuItem
              onClick={() => onEditStaff(staffId)}
              className="w-full px-4! py-2! text-left text-sm text-gray-700 hover:text-gray-700 focus:bg-gray-100! focus:text-gray-700!"
            >
              編集
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => setIsDeleteDialogOpen(true)}
              className="w-full px-4! py-2! text-left text-sm text-danger hover:text-danger focus:bg-gray-100! focus:text-danger!"
            >
              削除
            </DropdownMenuItem>
          </div>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog
        open={isDeleteDialogOpen}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) setIsDeleteDialogOpen(false);
        }}
      >
        <DialogContent className="w-full max-w-[600px] rounded-sm">
          <DialogTitle className="border-b border-solid border-gray-200 px-6 py-4 text-lg font-bold!">
            メンバーの削除
          </DialogTitle>
          <div className="p-6">
            <p>本当にこのメンバーを削除しますか？</p>
          </div>
          <div className="flex justify-end gap-4 border-t border-solid border-gray-200 px-6 py-4">
            <Button color="secondary" size="md" onClick={() => setIsDeleteDialogOpen(false)}>
              キャンセル
            </Button>
            <Button color="danger" size="md" onClick={handleDelete}>
              <div className="flex items-center gap-2">
                <MaterialIcon name="delete" />
                削除する
              </div>
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
